import { useId, useState, type MouseEvent } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import ManageAccountsOutlined from '@mui/icons-material/ManageAccountsOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import { useTranslations } from '@/i18n/use-translations';

type OrganizationTeamMemberRowMenuProps = {
  canManageRoles: boolean;
  isActiveMember: boolean;
  onManageRoles: () => void;
  onOpenHistory: () => void;
};

export default function OrganizationTeamMemberRowMenu({
  canManageRoles,
  isActiveMember,
  onManageRoles,
  onOpenHistory,
}: OrganizationTeamMemberRowMenuProps) {
  const t = useTranslations();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const showManage = canManageRoles && isActiveMember;

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleManageRoles = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    onManageRoles();
    handleClose();
  };

  const handleOpenHistory = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    onOpenHistory();
    handleClose();
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label={t('organization.team.memberRowActions')}
        aria-controls={anchorEl ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? 'true' : undefined}
        sx={{
          'color': 'text.secondary',
          'flexShrink': 0,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showManage ? (
          <MenuItem onClick={handleManageRoles}>
            <ListItemIcon>
              <ManageAccountsOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('organization.team.manageRoles')}</ListItemText>
          </MenuItem>
        ) : null}
        <MenuItem onClick={handleOpenHistory}>
          <ListItemIcon>
            <HistoryOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.details')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
